// Shared admin-tab metadata: the role gate and Dutch title for every
// /admin tab. Lives outside AdminPage.tsx so that other tabs (e.g. the
// "Rollen & rechten" reference page) can read the same maps without a
// circular import.

import { type AccessRole, ROLE_LEVEL } from "@/lib/hooks/useAuth";

export type Tab =
  | "overview"
  | "users"
  | "feedback"
  | "stats"
  | "visitors"
  | "templates"
  | "team_templates"
  | "organizations"
  | "departments"
  | "associations"
  | "audio_preview"
  | "logs"
  | "recordings"
  | "dept_recordings"
  | "recording_feedback"
  | "roles_permissions"
  | "rate_limits"
  | "signup_bans"
  | "tier"
  | "cluster"
  | "emails"
  | "todos"
  | "telemetry"
  | "it_overview"
  | "lexicon";

export const TAB_MIN_ROLE: Record<Tab, AccessRole> = {
  overview: "hospital_admin",
  users: "hospital_admin",
  associations: "hospital_admin",
  departments: "hospital_admin",
  organizations: "hospital_admin",
  feedback: "superuser",
  stats: "hospital_admin",
  visitors: "superuser",
  templates: "hospital_admin",
  team_templates: "hospital_admin",
  audio_preview: "hospital_admin",
  recordings: "superuser",
  dept_recordings: "superuser",
  recording_feedback: "superuser",
  logs: "superuser",
  roles_permissions: "superuser",
  rate_limits: "superuser",
  signup_bans: "superuser",
  tier: "superuser",
  cluster: "superuser",
  emails: "superuser",
  todos: "user",
  telemetry: "superuser",
  it_overview: "hospital_admin",
  lexicon: "superuser",
};

export const TAB_TITLE: Record<Tab, string> = {
  overview: "Overzicht",
  users: "Gebruikers",
  associations: "Associaties",
  departments: "Afdelingen",
  organizations: "Organisaties",
  feedback: "Feedback",
  stats: "Statistieken",
  visitors: "Bezoekers",
  templates: "Templates",
  team_templates: "Team-sjablonen",
  audio_preview: "Audio preview",
  recordings: "Opnames",
  dept_recordings: "Afdelingsopnames",
  recording_feedback: "Opname-feedback",
  logs: "Logs",
  roles_permissions: "Rollen & rechten",
  rate_limits: "Rate limits",
  signup_bans: "Blokkades",
  tier: "Tiers",
  cluster: "Cluster",
  emails: "E-mail",
  todos: "Todo's",
  telemetry: "Telemetrie",
  it_overview: "IT-overzicht",
  lexicon: "Lexicon",
};

// Explicit per-tab role allow-list. When a tab has an entry here, visibility
// is set membership in that list (NOT the hierarchical TAB_MIN_ROLE). This is
// the only way to show a tab to department_admin + afdeling_secretary while
// hiding it from hospital_admin/association_admin.
export const TAB_ALLOWED_ROLES: Partial<Record<Tab, AccessRole[]>> = {
  dept_recordings: ["superuser", "department_admin", "afdeling_secretary"],
  todos: ["user", "it_admin", "afdeling_secretary", "superuser"],
  it_overview: ["it_admin", "hospital_admin", "superuser"],
};

export function hasMinRole(current: AccessRole, required: AccessRole): boolean {
  return ROLE_LEVEL[current] >= ROLE_LEVEL[required];
}

export function isTabVisible(current: AccessRole, tab: Tab): boolean {
  const allow = TAB_ALLOWED_ROLES[tab];
  if (allow) return allow.includes(current);
  return hasMinRole(current, TAB_MIN_ROLE[tab]);
}

export function isTab(value: string): value is Tab {
  return value in TAB_MIN_ROLE;
}
